import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  conversion,
  detectSpikes,
  formatChange,
  rangeToDates,
  type DashboardFilters,
} from '@/lib/admin/dashboardFilters';

interface Pair { current: number; previous: number }
export interface OperationsData {
  headline: { page_views: Pair; sessions: Pair; signups: Pair; reviews_charged: Pair };
  funnel: { sessions: number; signups: number; confirmed: number; uploaded: number; charged: number };
  daily: Array<{ d: string; views: number; sessions: number; signups: number }>;
  countries: Array<{ country: string; views: number }>;
  credits: {
    spent: number; granted: number; expired: number; cost_eur: number;
    purchases: number; waiting: number; by_feature: Record<string, number>;
  };
  signup_risk: number;
}

const num = (v: unknown) => Number(v) || 0;
const fmt = (v: number, digits = 0) =>
  v.toLocaleString('en-GB', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

export const fetchOperations = async (f: DashboardFilters): Promise<OperationsData> => {
  const { from, to } = rangeToDates(f.range);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase.rpc as any)('admin_operations_overview', {
    p_from: from, p_to: to, p_humans_only: f.humansOnly, p_country: f.country,
  });
  if (error) throw error;
  return data as OperationsData;
};

const Headline: React.FC<{ label: string; pair?: Pair; loading: boolean }> = ({ label, pair, loading }) => (
  <Card>
    <CardHeader className="pb-1"><CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle></CardHeader>
    <CardContent>
      {loading || !pair ? <Skeleton className="h-8 w-20" /> : (
        <>
          <p className="text-3xl font-semibold tabular-nums">{fmt(num(pair.current))}</p>
          <p className="mt-1 text-xs text-muted-foreground">{formatChange(num(pair.current), num(pair.previous))}</p>
        </>
      )}
    </CardContent>
  </Card>
);

const OperationsOverview: React.FC<{ filters: DashboardFilters }> = ({ filters }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-operations', filters],
    queryFn: () => fetchOperations(filters),
    staleTime: 60_000,
  });

  if (error) return <p className="text-sm text-destructive">Could not load the operations overview.</p>;

  const spikes = data
    ? [
        ...detectSpikes(data.daily, 'views', 'Page views', 30),
        ...detectSpikes(data.daily, 'signups', 'Sign-ups', 3),
      ]
    : [];
  const f = data?.funnel;
  const steps = f
    ? [
        { label: 'Visits', value: num(f.sessions) },
        { label: 'Signed up', value: num(f.signups) },
        { label: 'Confirmed email', value: num(f.confirmed) },
        { label: 'Uploaded a paper', value: num(f.uploaded) },
        { label: 'Used credits', value: num(f.charged) },
      ]
    : [];
  const maxStep = Math.max(1, ...steps.map((s) => s.value));
  const c = data?.credits;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Headline label="Page views" pair={data?.headline.page_views} loading={isLoading} />
        <Headline label="Visits" pair={data?.headline.sessions} loading={isLoading} />
        <Headline label="Sign-ups" pair={data?.headline.signups} loading={isLoading} />
        <Headline label="Paid reviews" pair={data?.headline.reviews_charged} loading={isLoading} />
      </div>

      {data && (spikes.length > 0 || num(data.signup_risk) > 0) && (
        <Card className="border-destructive/40" role="status" aria-label="Spike alerts">
          <CardContent className="space-y-1 py-4">
            {spikes.map((s) => (
              <p key={`${s.metric}-${s.date}`} className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                {s.metric} on {s.date}: {fmt(s.value)}, usually around {fmt(s.typical)} a day.
              </p>
            ))}
            {num(data.signup_risk) > 0 && (
              <p className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                {data.signup_risk} blocked sign-up attempts in this period. Details below.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Sign-up funnel</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {isLoading ? <Skeleton className="h-40 w-full" /> : steps.map((s, i) => (
              <div key={s.label}>
                <div className="flex justify-between text-sm">
                  <span>{s.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {fmt(s.value)}{i > 0 && ` · ${conversion(s.value, steps[i - 1].value)}`}
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary" style={{ width: `${(s.value / maxStep) * 100}%` }} />
                </div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">Later steps count only people who signed up in this period.</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Credits</CardTitle></CardHeader>
          <CardContent>
            {isLoading || !c ? <Skeleton className="h-40 w-full" /> : (
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <dt className="text-muted-foreground">Spent</dt><dd className="text-right tabular-nums">{fmt(num(c.spent), 1)}</dd>
                <dt className="text-muted-foreground">Granted for free</dt><dd className="text-right tabular-nums">{fmt(num(c.granted), 1)}</dd>
                <dt className="text-muted-foreground">Expired</dt><dd className="text-right tabular-nums">{fmt(num(c.expired), 1)}</dd>
                <dt className="text-muted-foreground">Our AI cost</dt><dd className="text-right tabular-nums">EUR {fmt(num(c.cost_eur), 2)}</dd>
                <dt className="text-muted-foreground">Purchases</dt><dd className="text-right tabular-nums">{fmt(num(c.purchases))}</dd>
                <dt className="text-muted-foreground">Waiting for credits now</dt><dd className="text-right tabular-nums">{fmt(num(c.waiting))}</dd>
                {Object.entries(c.by_feature || {}).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-muted-foreground">Spent on {k.replace(/_/g, ' ')}</dt>
                    <dd className="text-right tabular-nums">{fmt(num(v), 1)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Top countries</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-24 w-full" /> : (data?.countries.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No country data yet. Countries are recorded for new page views only.</p>
          ) : (
            <ul className="grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
              {data!.countries.map((x) => (
                <li key={x.country} className="flex justify-between rounded-md bg-muted/50 px-3 py-2">
                  <span>{x.country === 'unknown' ? 'Unknown' : x.country}</span>
                  <span className="tabular-nums text-muted-foreground">{fmt(num(x.views))}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OperationsOverview;
